class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.prepareCartNote();
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
    });

    this.addEventListener(
      'transitionend',
      () => this.trapFocusInDrawer(),
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  prepareCartNote() {
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
  }

  trapFocusInDrawer() {
    const disclosure = [...document.querySelectorAll('cart-disclosure-modal[open]')].find((modal) => this.contains(modal.opener));
    if (disclosure) {
      disclosure.trapFocusInModal();
      return;
    }
    this.prepareCartNote();
    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty') || this.querySelector('.drawer__inner')
      : document.getElementById('CartDrawer');
    if (!containerToTrapFocusOn) return;
    const focusElement = this.classList.contains('is-empty')
      ? containerToTrapFocusOn?.querySelector('a, button') || containerToTrapFocusOn
      : this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
    trapFocus(containerToTrapFocusOn, focusElement);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  refreshContents(responseText) {
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const sourceDrawer = html.querySelector('cart-drawer');
    const sourceInner = sourceDrawer?.querySelector('.drawer__inner');
    const currentInner = this.querySelector('.drawer__inner');
    if (!sourceInner || !currentInner) throw new Error('Cart drawer section missing');
    const focusedElement = this.contains(document.activeElement) ? document.activeElement : null;
    const focusedLine = focusedElement?.closest('[data-cart-line-key]');
    const focusId = focusedElement?.id;
    const controlSelector = focusedLine
      ? `${focusedElement.localName}${[...focusedElement.classList].map((name) => `.${CSS.escape(name)}`).join('')}${focusedElement.name ? `[name="${CSS.escape(focusedElement.name)}"]` : ''}`
      : null;
    const controlIndex = focusedLine ? [...focusedLine.querySelectorAll(controlSelector)].indexOf(focusedElement) : -1;
    const lineKey = focusedLine?.dataset.cartLineKey;
    currentInner.replaceWith(sourceInner);
    this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
    this.prepareCartNote();
    if (this.classList.contains('active')) {
      this.trapFocusInDrawer();
      if (document.activeElement?.closest('cart-disclosure-modal[open]')) return;
      if (focusedLine) {
        const line = [...this.querySelectorAll('[data-cart-line-key]')].find((row) => row.dataset.cartLineKey === lineKey);
        line?.querySelectorAll(controlSelector)[controlIndex]?.focus();
      } else if (focusId) {
        this.querySelector(`#${CSS.escape(focusId)}`)?.focus();
      }
    }
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (this.classList.contains('active')) {
        this.trapFocusInDrawer();
      } else {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      ...(document.querySelector('cart-items')
        ? super.getSectionsToRender().filter((section) => section.section !== 'cart-drawer' && section.id !== 'cart-icon-bubble')
        : []),
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
